import React from 'react';
import { logger } from '@/lib/logger';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pencil, Check, X } from 'lucide-react';
import { useToast } from "@/components/ui/use-toast";
import { getAuthenticatedSupabaseClient } from "@/integrations/supabase/client";

interface FloorPlanNameEditorProps {
  floorPlanId: string;
  currentName: string;
  onNameUpdated: (newName: string) => void;
  variant?: 'inline' | 'button';
}

export function FloorPlanNameEditor({ 
  floorPlanId, 
  currentName, 
  onNameUpdated,
  variant = 'inline'
}: FloorPlanNameEditorProps) {
  const [isEditing, setIsEditing] = React.useState(false);
  const [newName, setNewName] = React.useState(currentName);
  const [isLoading, setIsLoading] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = async () => {
    if (!newName.trim() || newName.trim() === currentName) {
      setIsEditing(false);
      setNewName(currentName);
      return;
    }

    setIsLoading(true);

    try {
      const supabase = await getAuthenticatedSupabaseClient();
      const { error } = await supabase
        .from('floor_plans')
        .update({ name: newName.trim() })
        .eq('id', floorPlanId);

      if (error) throw error;

      onNameUpdated(newName.trim());
      toast({
        title: "Success",
        description: "Floor plan name updated successfully.",
      });
    } catch (error) {
      logger.error('Error updating floor plan name:', error);
      toast({
        title: "Error",
        description: "Failed to update floor plan name. Please try again.",
        variant: "destructive"
      });
      setNewName(currentName);
    } finally {
      setIsLoading(false);
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setNewName(currentName);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (variant === 'button' && !isEditing) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="h-8 px-2"
        onClick={() => setIsEditing(true)}
      >
        <Pencil className="h-4 w-4 mr-1" />
        Rename
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {isEditing ? (
        <>
          <Input
            ref={inputRef}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-8"
            disabled={isLoading}
          />
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            onClick={handleSave}
            disabled={isLoading}
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            onClick={handleCancel}
            disabled={isLoading}
          >
            <X className="h-4 w-4" />
          </Button>
        </>
      ) : (
        <div className="flex items-center gap-2">
          <span className="truncate">{currentName}</span>
          <Button
            variant="ghost"
            size="sm"
            className={`h-8 px-2 ${variant === 'inline' ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}
            onClick={() => setIsEditing(true)}
          >
            <Pencil className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
} 
